import { Observer, Camera, Vector3 } from '@babylonjs/core';
import { Processor } from '.';
import { Node } from '../Node';
import { MeshRenderer } from '../component';
import { BoxDimensions, Color } from '../../interfaces';
import { ComponentInnerState } from './Processor';
import { Transform, TransformPosition, TransformRotation, TransformScale } from 'src';

export interface MeshRendererInnerState extends ComponentInnerState {
	mesh?: string | BoxDimensions;
	visibility?: number;
	enableOverlay?: boolean;
	overlayColor?: Color;
	screenPosition: { x: number; y: number };
	viewObserver?: Observer<Camera> | null;
	projectionObserver?: Observer<Camera> | null;
}

export class MeshRendererProcessor extends Processor {
	private updateScreenPosition(component: MeshRenderer): void {
		const iState = this.getState(component) as MeshRendererInnerState;
		if (!iState || !iState.meshAssembly) {
			return;
		}

		const { transformNode } = iState.meshAssembly;
		const worldMatrix = transformNode.getWorldMatrix();
		const transformMatrix = this.engine.getTransformMatrix();
		let position = transformNode.position.clone();

		const { screenOffset } = component.state;
		if (screenOffset) {
			const offset = new Vector3(screenOffset.x, screenOffset.y, screenOffset.z);
			position = position.add(offset);
		}
		const viewport = this.engine.cameraManager.getCamera().viewport;
		const coordinates = Vector3.Project(position, worldMatrix, transformMatrix, viewport);
		const canvasRect = this.engine.getCanvas().getBoundingClientRect();
		const x = canvasRect.right * coordinates.x + (1 - coordinates.x) * canvasRect.x;
		const y = canvasRect.bottom * coordinates.y + (1 - coordinates.y) * canvasRect.y;
		iState.screenPosition = { x, y };
	}
	public start(component: MeshRenderer, node: Node): boolean {
		if (!component.state.mesh) {
			return false;
		}
		const iState: MeshRendererInnerState = { screenPosition: { x: 0, y: 0 } };
		iState.mesh = component.state.mesh;

		if (typeof component.state.mesh === 'string') {
			iState.meshAssembly = this.engine.meshFactory.cloneAssembly(component.state.mesh);
			iState.meshAssembly.transformNode.name = 'meshrenderer';
		} else {
			iState.meshAssembly = this.engine.meshFactory.createBox(
				Object.assign({ isVisible: false }, component.state.mesh),
				'box'
			);
		}

		node.addChildAssembly(iState.meshAssembly);

		this.setState(component, iState);

		component.getScreenPosition = (forceUpdate?: boolean): { x: number; y: number } => {
			if (forceUpdate) {
				this.updateScreenPosition(component);
			}
			return iState.screenPosition;
		};
		return true;
	}

	public enable(component: MeshRenderer): boolean {
		const iState = this.getState<MeshRendererInnerState>(component);
		if (!iState.meshAssembly || !iState.meshAssembly.isReady() || component.isDisabled) {
			return false;
		}

		iState.visibility = component.state.visibility;
		const transform = component.state.transform as Transform;
		iState.meshAssembly.visibility = iState.visibility as number;
		iState.meshAssembly.localPosition = transform.position as TransformPosition;
		iState.meshAssembly.localRotation = transform.rotation as TransformRotation;
		iState.meshAssembly.localScale = transform.scale as TransformScale;
		iState.meshAssembly.shouldRender = true;

		this.updateScreenPosition(component);

		const camera = this.engine.cameraManager.getCamera('Camera');
		iState.projectionObserver = camera.onProjectionMatrixChangedObservable.add(() =>
			this.updateScreenPosition(component)
		);
		iState.viewObserver = camera.onViewMatrixChangedObservable.add(() =>
			this.updateScreenPosition(component)
		);
		return true;
	}

	public disable(component: MeshRenderer): boolean {
		const iState = this.getState<MeshRendererInnerState>(component);
		if (iState.meshAssembly) {
			iState.meshAssembly.shouldRender = false;
			return true;
		}

		return false;
	}

	public onDestroy(component: MeshRenderer): boolean {
		const iState = this.getState<MeshRendererInnerState>(component);
		if (iState.meshAssembly) {
			iState.meshAssembly.shouldRender = false;
			iState.meshAssembly.destroy();

			// remove camera observers
			const camera = this.engine.cameraManager.getCamera('Camera');
			const { projectionObserver, viewObserver } = iState;
			if (projectionObserver) {
				camera.onProjectionMatrixChangedObservable.remove(projectionObserver);
			}
			if (viewObserver) {
				camera.onViewMatrixChangedObservable.remove(viewObserver);
			}

			this.deleteState(component);
			return true;
		}

		return false;
	}

	public updateState(component: MeshRenderer, node: Node): boolean {
		const iState = this.getState<MeshRendererInnerState>(component);
		if (iState.meshAssembly) {
			if (component.state.mesh !== iState.mesh) {
				return false;
			}

			if (component.state.visibility !== iState.visibility) {
				iState.visibility = component.state.visibility;
				iState.meshAssembly.visibility = component.state.visibility as number;
			}

			const { enableOverlay, overlayColor } = component.state;
			if (enableOverlay !== iState.enableOverlay) {
				iState.enableOverlay = enableOverlay;
				iState.meshAssembly.enableOverlay = enableOverlay as boolean;
			}

			if (overlayColor !== iState.overlayColor) {
				iState.overlayColor = overlayColor;
				iState.meshAssembly.overlayColor = overlayColor;
			}

			const transform = component.state.transform as Transform;
			iState.meshAssembly.localPosition = transform.position as TransformPosition;
			iState.meshAssembly.localRotation = transform.rotation as TransformRotation;
			iState.meshAssembly.localScale = transform.scale as TransformScale;

			node.addChildAssembly(iState.meshAssembly);

			return true;
		}

		return false;
	}
}
